import {endOfDay} from 'date-fns/endOfDay';
import {startOfDay} from 'date-fns/startOfDay';

import {TData, TPayload} from 'src/components/List';
import {EChannelType, TItemId} from 'src/constants';
import {CACHE_LIFETIME, ECacheTag} from 'src/models';
import {rootApi} from 'src/models/api';

import {TSmsChannel, TSubscription} from '../SmsChannel/definitions';
import {TLicenseData} from './LicensesTab';
import {
  TAvailableTariff,
  TCreateMigrationPayload,
  TLicense,
  TPrismMigrationPayload,
  TSubject,
  TSubjectMigrationRequest,
} from './MigrationTab/definitions';
import {TBilledEvent, TBilledEventData, TStornoFormData} from './PaymentsTab';
import {
  EPayKeeperPaymentStatus,
  TPayKeeperPayment,
  TPayKeeperPaymentPayload,
} from './PaymentsTab/definitions';
import {TTariffTransfer} from './definitions';

type TSmsSubscription = {
  operator: string;
  country: string;
  subscription?: TSubscription['subscription'];
};

const api = rootApi.injectEndpoints({
  overrideExisting: 'throw',
  endpoints: (builder) => ({
    getStornoData: builder.mutation<TBilledEventData, TBilledEvent['originalEventId']>({
      query: (eventId) => ({
        method: 'GET',
        url: `/billing/storno/find/${eventId}`,
        meta: {
          isShowError: true,
        },
      }),
    }),
    getTransactionData: builder.mutation<TBilledEventData, TBilledEvent['originalEventId']>({
      query: (eventId) => ({
        method: 'GET',
        url: `/billing/correction/update/find/${eventId}`,
      }),
    }),
    updateTransactionAmount: builder.mutation<
      TBilledEventData,
      {
        originalEventId: TBilledEvent['originalEventId'];
        newAmount: number;
        comment?: string;
      }
    >({
      query: (data) => ({
        method: 'POST',
        url: `/billing/correction/update`,
        data,
        meta: {
          isShowError: true,
          isShowSuccess: true,
          successMessageKey: 'List:notification.saveSuccess',
        },
      }),
    }),
    startStornoProcess: builder.mutation<
      TBilledEventData,
      Pick<TStornoFormData, 'originalEventId' | 'comment'>
    >({
      query: (data) => ({
        method: 'POST',
        url: '/billing/storno',
        data,
        meta: {
          isShowError: true,
        },
      }),
    }),
    cancelStornoProcess: builder.mutation<TBilledEventData, TBilledEvent['originalEventId']>({
      query: (eventId) => ({
        method: 'POST',
        url: '/billing/storno/cancel',
        data: {originalEventId: eventId},
        meta: {
          isShowError: true,
        },
      }),
    }),
    getPayKeeperPayments: builder.query<
      TData<TPayKeeperPayment>,
      TPayload<TPayKeeperPaymentPayload>
    >({
      query: ({userId, date, limit, offset}) => ({
        method: 'POST',
        url: '/paykeeper/support/orders/get-page',
        data: {
          userId,
          limit,
          offset,
          timeFrom: startOfDay(date),
          timeTo: endOfDay(date),
        },
        meta: {
          isShowError: true,
        },
      }),
      providesTags: [ECacheTag.ONETIME_PAYMENT],
      keepUnusedDataFor: CACHE_LIFETIME,
    }),
    checkPayKeeperPaymentStatus: builder.mutation<
      {oldStatus: EPayKeeperPaymentStatus; newStatus: EPayKeeperPaymentStatus},
      {userId: number; orderId: string}
    >({
      query: (params) => ({
        method: 'POST',
        url: '/paykeeper/support/orders/status-lookup',
        params,
        meta: {
          isShowError: true,
        },
      }),
    }),
    refundPayKeeperPayment: builder.mutation<
      void,
      {userId: number; orderId: string; refundAmount: number}
    >({
      query: (params) => ({
        method: 'POST',
        url: '/paykeeper/support/orders/refund',
        params,
        meta: {isShowError: true},
      }),
      invalidatesTags: [ECacheTag.ONETIME_PAYMENT],
    }),
    getLicenseRenewal: builder.mutation<
      TLicenseData,
      {
        licenseId: number | string;
        month: string;
        year: string;
      }
    >({
      query: (payload) => ({
        method: 'GET',
        url: '/admin/billing/license/renewal',
        params: {
          licenseId: payload.licenseId,
          period: `${payload.year}-${payload.month}`,
        },
        meta: {
          isShowError: true,
        },
      }),
    }),
    renewLicense: builder.mutation<
      TLicenseData,
      {
        licenseId: number | string;
        month: string;
        year: string;
        channelType: EChannelType;
      }
    >({
      query: (payload) => ({
        method: 'POST',
        url: `/billing/jobs/license/renewal/${payload.channelType.toUpperCase()}/period/${
          payload.year
        }-${payload.month}`,
        params: {
          licenseIds: payload.licenseId,
        },
        meta: {
          isShowError: true,
          isShowSuccess: true,
          successMessageKey: 'Corrections:licenses.notification',
        },
      }),
    }),

    getSmsChannelBySubject: builder.query<TSmsChannel, TItemId>({
      query: (subjectId) => ({
        method: 'GET',
        url: `/channel-profile/sms/support/by-subject/${subjectId}`,
        meta: {isShowError: true},
      }),
    }),
    addSmsChannelSubscription: builder.mutation<
      void,
      {subjectId: number; subscriptions: TSmsSubscription[]}
    >({
      query: (data) => ({
        method: 'POST',
        data,
        url: '/channel-profile/sms/support/add-subscriptions',
        meta: {isShowError: true},
      }),
      invalidatesTags: [ECacheTag.SMS_SUBSCRIPTIONS],
    }),
    deleteSmsChannelSubscription: builder.mutation<
      void,
      {subjectId: number; subscriptions: TSmsSubscription[]}
    >({
      query: (data) => ({
        method: 'DELETE',
        data,
        url: '/channel-profile/sms/support/delete-subscriptions',
        meta: {isShowError: true},
      }),
      invalidatesTags: [ECacheTag.SMS_SUBSCRIPTIONS],
    }),
    getMigrationSubjects: builder.query<TSubject[], {tenantId: TItemId; channelType: EChannelType}>(
      {
        query: ({tenantId, channelType}) => ({
          method: 'GET',
          url: '/channel-profile/subjects/support',
          params: {tenantId, channelType},
          meta: {isShowError: true},
        }),
      },
    ),
    getMigrationLicenses: builder.query<
      Record<number, number | undefined>,
      {subjectIds: number[]; tenantId: TItemId; channelType: EChannelType}
    >({
      queryFn: async ({subjectIds, tenantId, channelType}, _, __, baseQuery) => {
        const results = await Promise.all(
          subjectIds.map((subjectId) =>
            baseQuery({
              method: 'GET',
              url: '/admin/billing/license/active',
              params: {subjectId, tenantId, channelType},
              meta: {isShowError: true},
            }),
          ),
        );

        const failed = results.find(({error}) => error && error.status !== 404);

        if (failed?.error) {
          return {error: failed.error, meta: failed.meta};
        }

        return {
          data: Object.fromEntries(
            subjectIds.map((subjectId, index) => [
              subjectId,
              (results[index].data as TLicense | undefined)?.id,
            ]),
          ),
        };
      },
    }),
    getMigrationTariffs: builder.query<TAvailableTariff[], TItemId>({
      query: (tenantId) => ({
        method: 'GET',
        url: `/billing/tariffs/available/support/find-all/for-tenant/${tenantId}`,
        meta: {isShowError: true},
      }),
    }),
    getSubjectMigrations: builder.query<TSubjectMigrationRequest[], void>({
      query: () => ({
        method: 'GET',
        url: '/channel-profile/subject-migration',
        meta: {isShowError: true},
      }),
      providesTags: [ECacheTag.SUBJECT_MIGRATIONS],
      keepUnusedDataFor: CACHE_LIFETIME,
    }),
    getSubjectMigration: builder.query<TSubjectMigrationRequest, TItemId>({
      query: (id) => ({
        method: 'GET',
        url: `/channel-profile/subject-migration/${id}`,
        meta: {isShowError: true},
      }),
    }),
    createSubjectMigration: builder.mutation<TSubjectMigrationRequest, TCreateMigrationPayload>({
      query: (data) => ({
        method: 'POST',
        url: '/channel-profile/subject-migration',
        data,
        meta: {
          isShowError: true,
          isShowSuccess: true,
          successMessageKey: 'Corrections:migration.subjects.successNotification',
        },
      }),
      invalidatesTags: [ECacheTag.SUBJECT_MIGRATIONS],
    }),
    deleteSubjectMigration: builder.mutation<void, TItemId>({
      query: (id) => ({
        method: 'DELETE',
        url: `/channel-profile/subject-migration/${id}`,
        meta: {
          isShowError: true,
          isShowSuccess: true,
          successMessageKey: 'Corrections:migration.subjects.deleteSuccessNotification',
        },
      }),
      invalidatesTags: [ECacheTag.SUBJECT_MIGRATIONS],
    }),
    createPrismMigration: builder.mutation<void, TPrismMigrationPayload>({
      query: (data) => ({
        method: 'POST',
        url: '/channel-profile/prism/register-subject',
        data,
        meta: {
          isShowError: true,
          isShowSuccess: true,
          successMessageKey: 'Corrections:migration.prism.successNotification',
        },
      }),
    }),
    transferTariffs: builder.mutation<void, TTariffTransfer>({
      query: (data) => ({
        method: 'POST',
        data,
        url: '/billing/tariffs/available/change-catalog-tariff-and-license',
        meta: {
          isShowError: true,
          isShowSuccess: true,
          successMessageKey: 'Corrections:tariffs.successNotification',
        },
      }),
    }),
  }),
});

export const {
  // Payments
  useGetStornoDataMutation,
  useGetTransactionDataMutation,
  useStartStornoProcessMutation,
  useCancelStornoProcessMutation,
  useUpdateTransactionAmountMutation,
  useGetPayKeeperPaymentsQuery,
  useCheckPayKeeperPaymentStatusMutation,
  useRefundPayKeeperPaymentMutation,
  // Licenses
  useRenewLicenseMutation,
  useGetLicenseRenewalMutation,
  // Sms channel
  useGetSmsChannelBySubjectQuery,
  useAddSmsChannelSubscriptionMutation,
  useDeleteSmsChannelSubscriptionMutation,
  // Migration
  useGetMigrationSubjectsQuery,
  useGetMigrationLicensesQuery,
  useGetMigrationTariffsQuery,
  useGetSubjectMigrationsQuery,
  useCreateSubjectMigrationMutation,
  useDeleteSubjectMigrationMutation,
  useCreatePrismMigrationMutation,
  // Tariffs
  useTransferTariffsMutation,
} = api;
